import { ApiError } from "next/dist/server/api-utils";
import { toast } from "react-toastify";
import { PhotoState } from "./photo-uploader-reducer";
import { env } from "@/lib/env";

const PROFILESENDPOINTURL = env.NEXT_PROFILES_API_URL;
const LISTINGSMANAGEMENTENDPOINTURL = env.NEXT_LISTINGS_MANAGEMENT_API_URL;

export const handleUploadListingPhotos = async (
  listingId: string,
  photosArray: PhotoState[],
  idToken: string | null,
  dispatchUploader: (resizedImageSrc: string, distributionSrc: string) => void
) => {
  const imageKeys: string[] = photosArray
    .filter((photo) => !photo.isUploaded)
    .map((photo) => photo.file.name);
  if (imageKeys.length == 0) {
    return;
  }
  if (idToken === null) {
    toast("Please reauthenticate before performing this action.", {
      type: "error",
    });
    return;
  }
  try {
    const response = await fetch(
      `${PROFILESENDPOINTURL}/generateS3UploadLinks`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: idToken,
        },
        body: JSON.stringify({
          image_keys: [...imageKeys],
          folder_id: listingId,
          root_folder_id: "listings-photos/",
          bucket_name: env.NEXT_PUBLIC_LISTINGS_PHOTOS_BUCKET_NAME,
        }),
      }
    );
    if (!response.ok) {
      const apiErr = (await response.json()) as ApiError;
      throw new Error(apiErr.message);
    }
    const urls = (await response.json()) as URLS;
    await uploadPhotos(photosArray, urls);
    const photosPresignedUrls = Object.values(urls.urls);
    const s3Urls = photosPresignedUrls.map((url) => extractUrl(url));

    const databaseUploadResponse = await fetch(
      `${LISTINGSMANAGEMENTENDPOINTURL}/uploadListingPhotos`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: idToken,
        },
        body: JSON.stringify({
          listing_id: listingId,
          photo_urls: s3Urls,
        }),
      }
    );

    if (databaseUploadResponse.ok) {
      const responseJson =
        (await databaseUploadResponse.json()) as DatabaseUploadResponse;
      responseJson.photos_links.forEach((uploadedPhoto) => {
        const indexOfPhoto = photosArray.findIndex(
          (statePhoto) => uploadedPhoto.photo_id === statePhoto.file.name
        );
        if (indexOfPhoto >= 0) {
          dispatchUploader(
            photosArray[indexOfPhoto].resizedImageSrc,
            uploadedPhoto.photo_distribution_url
          );
        }
      });
    } else {
      const apiErr = (await databaseUploadResponse.json()) as ApiError;
      throw new Error(apiErr.message);
    }
    return;
  } catch (err) {
    const errorMessage =
      err instanceof Error ? err.message : "An unexpected error occurred.";
    toast(
      errorMessage === "Failed to fetch"
        ? "Unable to upload your photos."
        : errorMessage,
      {
        type: "error",
      }
    );
    return;
  }
};

export const handleDeletePhoto = async (
  listingId: string,
  photoDistributionSrc: string,
  idToken: string | null,
  deleteFromStateDispatch: () => void,
  addImageBackDispatch: () => void
) => {
  if (idToken === null) {
    toast("Please reauthenticate before performing this action.", {
      type: "error",
    });
    return;
  }
  try {
    deleteFromStateDispatch();
    const response = await fetch(
      `${LISTINGSMANAGEMENTENDPOINTURL}/deletePendingPhotoDynamoDB`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: idToken,
        },
        body: JSON.stringify({
          listing_id: listingId,
          delete_key: photoDistributionSrc,
        }),
      }
    );
    if (!response.ok) {
      const apiErr = (await response.json()) as ApiError;
      throw new Error(apiErr.message);
    }
  } catch (err) {
    addImageBackDispatch();
    const errorMessage =
      err instanceof Error ? err.message : "An unexpected error occurred.";
    toast(
      errorMessage === "Failed to fetch"
        ? "Unable to upload your photos."
        : errorMessage,
      {
        type: "error",
      }
    );
    return;
  }
};

const uploadPhotos = async (photoData: PhotoState[], urls: URLS) => {
  const uploadPromises = photoData
    .filter((photo) => !photo.isUploaded)
    .map(async (photo) => {
      const uploadResponse = await fetch(urls.urls[photo.file.name], {
        method: "PUT",
        headers: {
          "Content-Type": photo.file.type,
        },
        body: photo.file,
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed for ${photo.file.name}`);
      }

      return urls.urls[photo.file.name];
    });

  return Promise.all(uploadPromises);
};

interface URLS {
  urls: { [key: string]: string };
}

const extractUrl = (url: string): string | null => {
  const baseUrlRegex = /^(https:\/\/.*?\/.*?\/.*?\.jpeg)/;
  const match = url.match(baseUrlRegex);
  return match ? match[0] : null;
};

interface PhotoResponse {
  photo_id: string;
  photo_distribution_url: string;
}

interface DatabaseUploadResponse {
  photos_links: PhotoResponse[];
}
